import React, { useState, useRef, useEffect } from 'react';
import { Bot, X, Send, Sparkles, Check, ChevronDown, ChevronUp } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';

// ============================================
// Activity Creator Panel
// ============================================
// A slide-over panel for AI-guided activity creation.
// Supports: task, course, notice, survey, compliance
// Receives prefilled data from Main AI routing.
// ============================================

const CREATOR_TITLES = {
    task: 'Task Creator',
    course: 'Course Creator',
    notice: 'Notice Creator',
    survey: 'Survey Creator',
    compliance: 'Compliance Task Creator',
};

const CREATOR_COLORS = {
    task: 'from-blue-500 to-blue-600',
    course: 'from-emerald-500 to-emerald-600',
    notice: 'from-amber-500 to-amber-600',
    survey: 'from-purple-500 to-purple-600',
    compliance: 'from-red-500 to-red-600',
};

const CREATOR_DESCRIPTIONS = {
    task: 'I will help you create a task step by step. Tell me what you need.',
    course: 'Let me help you build a course. What should it cover?',
    notice: 'I will help you draft a notice. What do you want to announce?',
    survey: 'Let me help you create a survey. What feedback are you looking for?',
    compliance: 'I will guide you through creating a compliance task with proper enforcement.',
};

const ActivityCreatorPanel = ({ creatorType, prefilledData, onClose, onCreated }) => {
    const [messages, setMessages] = useState([]);
    const [inputMessage, setInputMessage] = useState('');
    const [isSending, setIsSending] = useState(false);
    const [activityData, setActivityData] = useState({});
    const [creatorStatus, setCreatorStatus] = useState('gathering');
    const [showPreview, setShowPreview] = useState(false);
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);

    // Auto-scroll to bottom
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    // Focus input on mount
    useEffect(() => {
        inputRef.current?.focus();
    }, []);

    // Send initial prefilled context message
    useEffect(() => {
        if (prefilledData && Object.keys(prefilledData).length > 0) {
            const contextMsg = buildPrefilledMessage(prefilledData);
            if (contextMsg) {
                sendMessage(null, contextMsg);
            }
        } else {
            // Add welcome message
            setMessages([{
                role: 'assistant',
                content: CREATOR_DESCRIPTIONS[creatorType] || 'What would you like to create?',
                status: 'gathering',
            }]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    function buildPrefilledMessage(data) {
        const parts = [];
        if (data.title) parts.push(`I want to create: "${data.title}"`);
        if (data.description) parts.push(`Description: ${data.description}`);
        if (data.phase) parts.push(`Phase: ${data.phase}`);
        if (data.category) parts.push(`Category: ${data.category}`);
        if (data.priority) parts.push(`Priority: ${data.priority}`);
        return parts.length > 0 ? parts.join('. ') : null;
    }

    async function sendMessage(e, overrideMessage = null) {
        if (e) e.preventDefault();
        const userMessage = overrideMessage || inputMessage.trim();
        if (!userMessage) return;

        if (!overrideMessage) setInputMessage('');

        // Add user message
        setMessages(prev => [...prev, {
            role: 'user',
            content: userMessage,
        }]);

        setIsSending(true);

        try {
            const response = await api.post(`/admin/ai/creator/${creatorType}/chat`, {
                message: userMessage,
                chat_history: messages.map(m => ({ role: m.role, content: m.content })),
                prefilled_data: prefilledData,
            });

            const data = response.data;

            if (data.success) {
                const resp = data.response;

                setMessages(prev => [...prev, {
                    role: 'assistant',
                    content: resp.message,
                    status: resp.status,
                    suggestions: resp.suggestions,
                }]);

                setCreatorStatus(resp.status);

                if (resp.activity_data && Object.keys(resp.activity_data).length > 0) {
                    setActivityData(prev => ({ ...prev, ...resp.activity_data }));
                }

                // Auto-show preview when ready
                if (resp.status === 'ready') {
                    setShowPreview(true);
                }
            }
        } catch (error) {
            console.error('Creator chat error:', error);
            setMessages(prev => [...prev, {
                role: 'assistant',
                content: 'Something went wrong. Could you try again?',
                status: 'gathering',
            }]);
        } finally {
            setIsSending(false);
        }
    }

    function handleSuggestionClick(suggestion) {
        sendMessage(null, suggestion);
    }

    function handleConfirmCreate() {
        // Send confirmation message
        sendMessage(null, 'Yes, create it.');
    }

    return (
        <div className="fixed inset-y-0 right-0 w-full max-w-md bg-card border-l border-border shadow-2xl z-50 flex flex-col animate-slide-in-right">
            {/* Header */}
            <div className={`flex items-center justify-between px-4 py-3 bg-gradient-to-r ${CREATOR_COLORS[creatorType] || 'from-blue-500 to-blue-600'} text-white`}>
                <div className="flex items-center gap-2">
                    <Sparkles size={20} />
                    <span className="font-semibold">{CREATOR_TITLES[creatorType] || 'Activity Creator'}</span>
                    {creatorStatus === 'ready' && (
                        <span className="px-2 py-0.5 bg-white/20 rounded-full text-xs">Ready</span>
                    )}
                </div>
                <button
                    onClick={onClose}
                    className="p-1.5 rounded-lg hover:bg-white/20 transition-colors cursor-pointer"
                >
                    <X size={18} />
                </button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {messages.map((msg, index) => (
                    <div key={index} className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : ''}`}>
                        {msg.role === 'assistant' && (
                            <div className={`w-7 h-7 min-w-[28px] rounded-full bg-gradient-to-br ${CREATOR_COLORS[creatorType]} flex items-center justify-center text-white`}>
                                <Bot size={14} />
                            </div>
                        )}
                        <div className={`max-w-[85%] px-4 py-3 rounded-2xl text-sm leading-relaxed
                            ${msg.role === 'user'
                                ? 'bg-primary text-white rounded-br-md'
                                : 'bg-muted/50 border border-border text-foreground rounded-bl-md'}
                        `}>
                            <p className="m-0 whitespace-pre-wrap">{msg.content}</p>

                            {/* Suggestions */}
                            {msg.suggestions && msg.suggestions.length > 0 && (
                                <div className="flex flex-wrap gap-1.5 mt-3 pt-3 border-t border-border/30">
                                    {msg.suggestions.map((s, i) => (
                                        <button
                                            key={i}
                                            onClick={() => handleSuggestionClick(s)}
                                            className="px-3 py-1.5 bg-card border border-border rounded-lg text-xs text-muted-foreground hover:text-foreground hover:border-primary/50 cursor-pointer transition-all"
                                        >
                                            {s}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                ))}

                {/* Thinking indicator */}
                {isSending && (
                    <div className="flex gap-3">
                        <div className={`w-7 h-7 min-w-[28px] rounded-full bg-gradient-to-br ${CREATOR_COLORS[creatorType]} flex items-center justify-center text-white`}>
                            <Bot size={14} />
                        </div>
                        <div className="flex items-center gap-2 px-4 py-3 bg-muted/50 border border-border rounded-2xl rounded-bl-md">
                            <div className="w-1.5 h-1.5 bg-primary rounded-full animate-pulse"></div>
                            <span className="text-sm text-muted-foreground italic">Working on it...</span>
                        </div>
                    </div>
                )}

                <div ref={messagesEndRef} />
            </div>

            {/* Activity Preview (collapsible) */}
            {Object.keys(activityData).length > 0 && (
                <div className="border-t border-border">
                    <button
                        onClick={() => setShowPreview(!showPreview)}
                        className="w-full flex items-center justify-between px-4 py-2.5 bg-background hover:bg-muted/50 text-sm text-foreground cursor-pointer transition-colors"
                    >
                        <span className="font-medium">Activity Preview</span>
                        {showPreview ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
                    </button>
                    {showPreview && (
                        <div className="px-4 pb-3 bg-background max-h-48 overflow-y-auto">
                            <div className="space-y-1.5 text-sm">
                                {Object.entries(activityData).map(([key, value]) => {
                                    if (value === null || value === undefined || value === '') return null;
                                    const displayValue = typeof value === 'object' ? JSON.stringify(value) : String(value);
                                    return (
                                        <div key={key} className="flex gap-2">
                                            <span className="text-muted-foreground capitalize min-w-[100px]">{key.replace(/_/g, ' ')}:</span>
                                            <span className="text-foreground font-medium truncate">{displayValue}</span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Confirm Create button (when ready) */}
            {creatorStatus === 'ready' && (
                <div className="px-4 py-3 border-t border-border bg-green-50">
                    <button
                        onClick={handleConfirmCreate}
                        disabled={isSending}
                        className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-green-600 hover:bg-green-700 border-none rounded-xl font-medium text-sm text-white cursor-pointer transition-all disabled:opacity-60"
                    >
                        <Check size={16} />
                        Create {CREATOR_TITLES[creatorType]?.replace(' Creator', '') || 'Activity'}
                    </button>
                </div>
            )}

            {/* Input */}
            <form className="flex gap-2 p-3 bg-card border-t border-border" onSubmit={sendMessage}>
                <input
                    ref={inputRef}
                    type="text"
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    placeholder="Describe what you need..."
                    disabled={isSending}
                    className="flex-1 px-4 py-2.5 bg-background border border-border rounded-xl text-sm text-foreground outline-none focus:border-primary/50 focus:ring-2 focus:ring-primary/20 placeholder:text-muted-foreground disabled:opacity-50"
                />
                <button
                    type="submit"
                    disabled={!inputMessage.trim() || isSending}
                    className={`w-10 h-10 rounded-xl bg-gradient-to-r ${CREATOR_COLORS[creatorType]} border-none text-white cursor-pointer flex items-center justify-center transition-all hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                    <Send size={16} />
                </button>
            </form>
        </div>
    );
};

export default ActivityCreatorPanel;
